import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, Check, ChevronDown, Clock, Copy, FileImage,
  FileText, Loader2, Mail, Mic, Music4, Send, ShieldCheck, Wallet, X, Zap,
} from "lucide-react";
import { compressReceipt, postFormWithRetry } from "@/lib/receipt";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";

export function openCreateSong() {
  window.dispatchEvent(new Event("episnya:open-create"));
}

export function openResumeSong(payload) {
  window.dispatchEvent(new CustomEvent("episnya:resume-create", { detail: payload || null }));
}

export function openWizard(mode) {
  window.dispatchEvent(new CustomEvent("episnya:open-wizard",
    { detail: { mode: mode || "bot" } }));
}

let CONSTRUCTOR_CATALOG = null;
async function loadConstructorCatalog() {
  if (CONSTRUCTOR_CATALOG) return CONSTRUCTOR_CATALOG;
  const r = await api.get("/public/constructor");
  CONSTRUCTOR_CATALOG = r.data || {};
  return CONSTRUCTOR_CATALOG;
}

const catLabel = (list, key, lang) => {
  const item = (list || []).find((x) => x.key === key);
  return item ? (item[lang] || item.ua || key) : (key || "—");
};

const stripEmoji = (s) => (s || "")
  .replace(/[\p{Extended_Pictographic}\u{FE0F}\u{20E3}]/gu, "").trim();

const cardCls = (active) => `relative w-full text-left rounded-2xl border backdrop-blur-md transition-all ${active
  ? "border-[#2BA8FF] bg-[#2BA8FF]/10"
  : "border-white/12 bg-white/[0.05] hover:border-white/30 hover:bg-white/[0.08]"}`;

const specialCardCls = (active) => `relative w-full text-left rounded-2xl border backdrop-blur-md transition-all ${active
  ? "border-[#2BA8FF] bg-[#2BA8FF]/20"
  : "border-[#2BA8FF]/35 bg-[#2BA8FF]/10 hover:border-[#2BA8FF]/60 hover:bg-[#2BA8FF]/15"}`;

const ONE_LINE = "block whitespace-nowrap overflow-hidden pr-7";

const BriefRow = ({ icon, label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="flex items-center gap-2.5 text-[11px] text-[#647089] font-mono uppercase tracking-wider">
      {icon} {label}
    </span>
    <span className="text-sm font-semibold text-right">{value}</span>
  </div>
);

const SOUND_KEYS = ["guitar", "pop", "rnb", "dance", "zastilna", "lightrock", "rock", "rap", "deephouse", "disney", "disco"];
const SOUND_ICONS = ["🪕", "🎤", "🎷", "💃", "🥂", "🎸", "🤘", "🎧", "🌊", "🏰", "🪩"];
const VOICE_KEYS = ["male", "female", "duet"];
const VOICE_ICONS = ["👨", "👩", "👫"];
const DELIVERY_KEYS = ["telegram", "whatsapp", "viber", "email", "instagram"];
const DELIVERY_KEYS_EN = ["email", "telegram", "whatsapp", "instagram"];
const SOCIAL_ICONS = [
  "/icons/social/telegram.svg",
  "/icons/social/whatsapp.svg",
  "/icons/social/viber.svg",
  "/icons/social/mail.svg",
  "/icons/social/instagram.svg",
];
const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const PHONE_RE = /^\+?\d{9,15}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const TELEGRAM_RE = /^@?[A-Za-z][A-Za-z0-9_]{4,31}$/;
const INSTAGRAM_RE = /^@?[A-Za-z0-9._]{1,30}$/;
const normalizePhone = (v) => (v || "").replace(/[\s()\-.]/g, "");

const isContactValid = (method, value) => {
  const v = (value || "").trim();
  if (!v) return false;
  if (method === "email") return EMAIL_RE.test(v);
  if (method === "instagram") return INSTAGRAM_RE.test(v);
  if (method === "telegram") {
    return TELEGRAM_RE.test(v) || PHONE_RE.test(normalizePhone(v));
  }
  return PHONE_RE.test(normalizePhone(v));
};

const contactErrorKey = (method, value) => {
  const v = (value || "").trim();
  if (!v) return null;
  if (method === "email") return EMAIL_RE.test(v) ? null : "err_email";
  if (method === "instagram") return INSTAGRAM_RE.test(v) ? null : "err_instagram";
  if (method === "telegram") {
    return (TELEGRAM_RE.test(v) || PHONE_RE.test(normalizePhone(v))) ? null : "err_telegram";
  }
  return PHONE_RE.test(normalizePhone(v)) ? null : "err_phone";
};

const CLIENT_ID_KEY = "episnya_client_id";
const getRefCode = () => {
  try { return sessionStorage.getItem("ref_code") || ""; } catch { return ""; }
};
const getClientId = () => {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const track = (source, detail) => {
  try {
    api.post("/web/click", detail ? { source, detail } : { source }).catch(() => {});
  } catch { /* noop */ }
};

const OptionCard = ({ title, desc, price, badge, accent, onClick, testid, bg, corner }) => (
  <button data-testid={testid} onClick={onClick}
    className="relative text-left rounded-3xl p-5 pt-9 border border-white/10 hover:border-[#2BA8FF]/60 transition-all lift group outline-none focus:outline-none"
    style={bg || { backgroundColor: "#0B1220" }}>
    <span className="absolute -top-3 right-3 rotate-[8deg] rounded-xl px-3.5 py-1.5 text-base font-display font-extrabold shadow-xl"
      style={accent === "yellow"
        ? { background: "linear-gradient(135deg,#FFD400,#FFB300)", color: "#060A14" }
        : { background: "linear-gradient(135deg,#2BA8FF,#1E86E8)", color: "#fff" }}>
      {price}
    </span>
    <span className="absolute top-3 left-5 text-[10px] font-mono uppercase tracking-wider text-[#647089]">{badge}</span>
    <h3 className="font-display font-extrabold uppercase text-lg leading-tight">{title}</h3>
    <p className="text-sm text-[#9FB0C7] mt-2 leading-relaxed">{desc}</p>
    <span className="mt-4 flex items-end justify-between">
      <span className="inline-flex items-center text-[#2BA8FF] group-hover:translate-x-1 transition-transform">
        <ArrowRight size={17} />
      </span>
      {corner || null}
    </span>
  </button>
);

const VOICE_EMOJI = { male: "👨", female: "👩", child: "🧒", duet: "👫" };

function Wizard({ open, onClose, initial, mode = "bot" }) {
  const { t, lang, enSite } = useLang();
  const c = t.create;
  const deliveryKeys = enSite ? DELIVERY_KEYS_EN : DELIVERY_KEYS;
  const [step, setStep] = useState(0);   // 0 вибір (2 сторінки: звучання → виконавець) · 1 історія · 2 доставка · 3 оплата · 4 готово
  const [pickPage, setPickPage] = useState("sound"); // підсторінки кроку 1: sound → voice (прогрес не змінюється)
  const [sound, setSound] = useState(null);
  const [voice, setVoice] = useState(null);
  // Детальний флоу «як у боті» (режим bot): привід → жанр → настрій → мова → голос
  const [cat, setCat] = useState(null);
  const [bPage, setBPage] = useState("event");
  const [bEvent, setBEvent] = useState(null);
  const [bEventText, setBEventText] = useState("");
  const [bGenre, setBGenre] = useState(null);
  const [bMix, setBMix] = useState([]);
  const [bGenreText, setBGenreText] = useState("");
  const [bMood, setBMood] = useState(null);
  const [bMoodText, setBMoodText] = useState("");
  const [bLang, setBLang] = useState(enSite ? "en" : "uk");
  const [bVoice, setBVoice] = useState(null);
  const [pulseNext, setPulseNext] = useState(false);
  const [tier, setTier] = useState("standard");
  const [desc, setDesc] = useState("");
  const [delivery, setDelivery] = useState(enSite ? "email" : "telegram");
  const [contact, setContact] = useState("");
  const [showExample, setShowExample] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);
  const [card, setCard] = useState("");
  const [cardLoading, setCardLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [preview, setPreview] = useState("");
  const [walletProvider, setWalletProvider] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletFailed, setWalletFailed] = useState(false);
  const [walletFormOpen, setWalletFormOpen] = useState(false);
  const [walletEmail, setWalletEmail] = useState("");
  const [walletMessage, setWalletMessage] = useState("");
  const [walletSending, setWalletSending] = useState(false);
  const [walletSent, setWalletSent] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [transition, setTransition] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const fileRef = useRef(null);
  const advanceRef = useRef(null);
  const walletTimerRef = useRef(null);
  const later = (fn) => {
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(fn, 220);
  };
  const cancelAdvance = () => clearTimeout(advanceRef.current);

  useEffect(() => () => clearTimeout(walletTimerRef.current), []);

  useEffect(() => {
    if (open) {
      clearTimeout(advanceRef.current);
      clearTimeout(walletTimerRef.current);
      setStep(0); setPickPage("sound"); setSound(null); setVoice(null); setDesc(""); setDelivery(enSite ? "email" : "telegram");
      setContact(""); setShowExample(false); setShowBrief(false); setGuaranteeOpen(false);
      setCard(""); setReceipt(null); setPreview("");
      setWalletProvider(null); setWalletBusy(false); setWalletFailed(false);
      setWalletFormOpen(false); setWalletEmail(""); setWalletMessage("");
      setWalletSending(false); setWalletSent(false); setWalletError("");
      setError(""); setSubmitting(false); setTransition(false);
      setOrderNumber(null); setCopied(false);
      setBPage("event"); setBEvent(null); setBEventText(""); setBGenre(null);
      setBMix([]); setBGenreText(""); setBMood(null); setBMoodText("");
      setBLang(enSite ? "en" : "uk"); setBVoice(null); setPulseNext(false); setTier("standard");
      if (mode === "bot") loadConstructorCatalog().then(setCat).catch(() => setCat({}));
      // Резюм после виджета «Ваше останнє створення» (только студийный флоу).
      if (initial && mode === "studio") {
        const si = SOUND_KEYS.indexOf(initial.genre);
        const vi = VOICE_KEYS.indexOf(initial.voice);
        if (si >= 0) setSound(si);
        if (vi >= 0) setVoice(vi);
        if (initial.description) setDesc(initial.description);
        if (deliveryKeys.includes(initial.delivery)) setDelivery(initial.delivery);
        if (initial.contact) setContact(initial.contact);
        const st = Math.max(0, Math.min(3, Number(initial.step) || 2));
        setStep(st);
        if (st === 0) setPickPage(si >= 0 ? "voice" : "sound");
      }
      // Резюм детального флоу «як у боті»: восстанавливаем все ответы и шаг.
      if (initial && mode === "bot") {
        if (initial.event) setBEvent(initial.event);
        if (initial.event_text) setBEventText(initial.event_text);
        if (initial.genre) setBGenre(initial.genre);
        if (Array.isArray(initial.genre_mix)) setBMix(initial.genre_mix.slice(0, 2));
        if (initial.genre_text) setBGenreText(initial.genre_text);
        if (initial.mood) setBMood(initial.mood);
        if (initial.mood_text) setBMoodText(initial.mood_text);
        if (initial.song_language && !enSite) setBLang(initial.song_language);
        if (initial.voice) setBVoice(initial.voice);
        if (initial.description) setDesc(initial.description);
        if (deliveryKeys.includes(initial.delivery)) setDelivery(initial.delivery);
        if (initial.contact) setContact(initial.contact);
        const st = Math.max(0, Math.min(3, Number(initial.step) || 2));
        setStep(st);
      }
    }
  }, [open, initial, mode, enSite, deliveryKeys]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, submitting, onClose]);

  useEffect(() => {
    if (!open || step !== 3 || enSite || card || cardLoading) return;
    setCardLoading(true);
    api.get("/public/payment-card")
      .then((r) => setCard(r.data?.card || ""))
      .catch(() => setCard(""))
      .finally(() => setCardLoading(false));
  }, [open, step, card, cardLoading, enSite]);

  // Лид: только когда юзер дошёл до этапа карты (оплата) — один раз за сессию.
  // FB → Meta CAPI, TikTok → Events API, партнерская лінка (ref) → лид сайта.
  const leadFired = useRef(false);
  useEffect(() => {
    if (!open || step !== 3 || leadFired.current) return;
    let fbclid = "";
    let ttclid = "";
    let ref = "";
    try {
      fbclid = sessionStorage.getItem("fb_clid") || "";
      ttclid = sessionStorage.getItem("tt_clid") || "";
      ref = sessionStorage.getItem("ref_code") || "";
    } catch { /* ignore */ }
    if (!fbclid && !ttclid && !ref) return;
    leadFired.current = true;
    if (fbclid) api.post("/fb/lead", { fbclid, source: "site_pay" }).catch(() => {});
    if (ttclid) api.post("/tt/lead", { ttclid, source: "site_pay" }).catch(() => {});
    if (ref) {
      let cid = "";
      try { cid = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
      api.post("/ref/lead", { ref, client_id: cid }).catch(() => {});
    }
  }, [open, step]);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const pickReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")
        || f.size > MAX_RECEIPT_BYTES) {
      setError(c.err_receipt);
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setReceipt(f);
    setPreview(URL.createObjectURL(f));
    setError("");
    const small = await compressReceipt(f);
    if (small !== f) setReceipt(small);
  };

  const copyCard = async () => {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const contactOk = delivery ? isContactValid(delivery, contact) : false;
  const contactProblem = delivery ? contactErrorKey(delivery, contact) : null;

  const allGenres = [...(cat?.genres_main || []), ...(cat?.genres_extra || [])];
  const BOT_BACK = { genre: "event", more: "genre", mix: "genre", custom: "genre",
                     mood: "genre", lang: "mood", voice: "lang" };
  const EV_TOP = ["custom", "nooccasion"];
  const eventsOrdered = [...(cat?.events || [])].sort((a, b) => {
    const ai = EV_TOP.indexOf(a.key);
    const bi = EV_TOP.indexOf(b.key);
    return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
  });
  const BOT_STEP = { event: 1, genre: 2, more: 2, mix: 2, custom: 2,
                     mood: 3, lang: 4, voice: 5 };
  const headerStep = mode === "bot"
    ? (step === 0 ? (BOT_STEP[bPage] || 1) : step + 5)
    : step + 1;
  const totalSteps = mode === "bot" ? 8 : 4;
  const tiers = [
    { key: "standard", price: c.tier_standard_price, label: c.tier_standard, eta: c.tier_standard_eta },
    { key: "express", price: c.tier_express_price, label: c.tier_express, eta: c.tier_express_eta },
    { key: "urgent", price: c.tier_urgent_price, label: c.tier_urgent, eta: c.tier_urgent_eta },
  ];
  const activeTier = tiers.find((x) => x.key === tier) || tiers[0];

  const orderPayload = () => mode === "bot"
    ? {
        mode,
        site_locale: enSite ? "en" : "ua",
        genre: bGenre || "pop",
        voice: bVoice || "female",
        event: bEvent || "",
        event_text: bEvent === "custom" ? bEventText.trim() : "",
        mood: bMood || "",
        mood_text: bMood === "custom" ? bMoodText.trim() : "",
        song_language: bLang || "uk",
        genre_mix: bGenre === "mix" ? bMix : [],
        genre_text: bGenre === "custom" ? bGenreText.trim() : "",
      }
    : { mode, genre: SOUND_KEYS[sound], voice: VOICE_KEYS[voice], tier,
        site_locale: enSite ? "en" : "ua" };

  const goBack = () => {
    cancelAdvance();
    setError("");
    if (step > 0) { setStep((s) => s - 1); return; }
    if (mode === "bot") setBPage((p) => BOT_BACK[p] || "event");
    else setPickPage("sound");
  };

  const confirmCheckout = () => {
    if (transition) return;
    setError("");
    track("create_checkout", `${c.methods[delivery]} · ${contact.trim()}`);
    api.post("/public/landing/unpaid", {
      ...orderPayload(),
      description: desc.trim(),
      delivery,
      contact: contact.trim(),
      client_id: getClientId(),
      ref: getRefCode(),
    }).catch(() => {});
    setTransition(true);
    setTimeout(() => {
      setTransition(false);
      setStep(3);
    }, 1200);
  };

  const startWalletPayment = (provider) => {
    if (walletBusy || walletSent) return;
    const payload = orderPayload();
    setWalletProvider(provider);
    setWalletBusy(true);
    setWalletFailed(false);
    setWalletFormOpen(false);
    setWalletEmail(contact.trim());
    setWalletError("");
    track("wallet_pay_click", provider);
    api.post("/public/wallet-intent", {
      provider,
      contact: contact.trim(),
      client_id: getClientId(),
      page: window.location.href,
      genre: payload.genre,
      voice: payload.voice,
      description: desc.trim(),
    }).catch(() => {});
    clearTimeout(walletTimerRef.current);
    walletTimerRef.current = setTimeout(() => {
      setWalletBusy(false);
      setWalletFailed(true);
    }, 2600);
  };

  const submitWalletLead = async () => {
    if (walletSending) return;
    const email = walletEmail.trim();
    const message = walletMessage.trim();
    if (!EMAIL_RE.test(email)) { setWalletError(c.wallet_err_email); return; }
    if (message && message.length < 10) { setWalletError(c.wallet_err_message); return; }
    setWalletSending(true);
    setWalletError("");
    try {
      await api.post("/public/wallet-lead", {
        provider: walletProvider,
        email,
        message: message || undefined,
        contact: email,
        client_id: getClientId(),
        page: window.location.href,
      });
      setWalletSent(true);
    } catch {
      setWalletError(c.wallet_err_send);
    } finally {
      setWalletSending(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    if (!receipt) { setError(c.err_receipt); return; }
    if (!delivery || !contactOk) {
      setError(contactProblem ? c[contactProblem] : c.err_contact);
      return;
    }
    if (desc.trim().length < 25) { setError(c.err_desc); return; }
    setSubmitting(true);
    setUploadPct(0); setError("");
    track("create_sent");
    try {
      const payload = orderPayload();
      const fd = new FormData();
      fd.append("genre", payload.genre);
      fd.append("voice", payload.voice);
      fd.append("description", desc.trim());
      fd.append("delivery", delivery);
      fd.append("contact", contact.trim());
      fd.append("client_id", getClientId());
      fd.append("mode", payload.mode);
      fd.append("site_locale", payload.site_locale);
      if (mode === "studio") fd.append("tier", payload.tier || "standard");
      try {
        const fbc = sessionStorage.getItem("fb_clid") || "";
        if (fbc) fd.append("fbclid", fbc);
      } catch { /* ignore */ }
      try {
        const ttc = sessionStorage.getItem("tt_clid") || "";
        if (ttc) fd.append("ttclid", ttc);
      } catch { /* ignore */ }
      if (mode === "bot") {
        fd.append("event", payload.event);
        fd.append("event_text", payload.event_text);
        fd.append("mood", payload.mood);
        fd.append("mood_text", payload.mood_text);
        fd.append("song_language", payload.song_language);
        fd.append("genre_mix", JSON.stringify(payload.genre_mix || []));
        fd.append("genre_text", payload.genre_text);
      }
      fd.append("receipt", receipt);
      const r = await postFormWithRetry("/public/landing/order", fd, { onProgress: setUploadPct });
      setOrderNumber(r.data?.number);
      setStep(4);
      // Виджет со статусом появляется сразу после прикрепления чека.
      window.dispatchEvent(new Event("episnya:widget-refresh"));
    } catch {
      setError(c.err_send);
    } finally {
      setSubmitting(false);
      setUploadPct(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-[#060A14] overflow-y-auto" data-testid="create-song-wizard" role="dialog" aria-modal="true">
      <div className="pointer-events-none fixed inset-0 hero-flag opacity-70" />
      <div className="sticky top-0 z-10 bg-[#060A14]/80 backdrop-blur"
        style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}>
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {step < 4 && (step > 0 || (mode === "bot" ? bPage !== "event" : pickPage === "voice")) && (
              <button data-testid="wizard-back-arrow"
                onClick={goBack}
                aria-label={c.back}
                className="grid place-items-center w-8 h-8 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors shrink-0">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-[#9FB0C7] font-mono uppercase tracking-wider truncate">
              <Music4 size={15} className="text-[#2BA8FF] shrink-0" />
              {step < 4 ? (mode === "bot" ? c.step_of8 : c.step_of).replace("{n}", headerStep) : c.done_title}
            </div>
          </div>
          <button data-testid="create-song-close" onClick={onClose}
            className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors"
            aria-label={c.done_close}>
            <X size={18} />
          </button>
        </div>
        {step < 4 && (
          <div className="max-w-2xl mx-auto px-5 pb-3 flex gap-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${i < headerStep ? "bg-[#2BA8FF]" : "bg-white/10"}`} />
            ))}
          </div>
        )}
      </div>

      <div className="relative max-w-2xl mx-auto px-5 pt-8 sm:pt-12"
        style={{ paddingBottom: "max(140px, calc(env(safe-area-inset-bottom) + 100px))" }}>
        {step === 0 && mode !== "bot" && pickPage === "sound" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.sound_title}</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">{c.sound_sub}</p>
            <div className="mt-6 space-y-3">
              {c.sounds.map((s, i) => {
                const active = sound === i;
                return (
                  <button key={SOUND_KEYS[i]} data-testid={`wizard-sound-${SOUND_KEYS[i]}`}
                    onClick={() => {
                      setSound(i);
                      setError("");
                      track("create_sound", c.sounds[i].t);
                      later(() => setPickPage((p) => (p === "sound" ? "voice" : p)));
                    }}
                    className={`w-full text-left rounded-2xl border p-4 flex gap-3.5 items-start transition-all ${active
                      ? "border-[#2BA8FF] bg-[#0E1B33]"
                      : "border-white/10 bg-[#0B1220] hover:border-white/25"}`}>
                    <span className="text-2xl leading-none mt-0.5">{SOUND_ICONS[i]}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-bold">{s.t}</span>
                      <span className="block text-sm text-[#9FB0C7] mt-1 leading-relaxed">{s.d}</span>
                    </span>
                    <span className={`grid place-items-center w-6 h-6 rounded-full border shrink-0 mt-0.5 ${active
                      ? "border-[#2BA8FF] bg-[#2BA8FF]" : "border-white/20"}`}>
                      {active && <Check size={14} className="text-white" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 0 && mode !== "bot" && pickPage === "voice" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.vo_title}</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">{c.vo_sub}</p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {c.voices.map((v, i) => {
                const active = voice === i;
                return (
                  <button key={VOICE_KEYS[i]} data-testid={`wizard-voice-${VOICE_KEYS[i]}`}
                    onClick={() => {
                      setVoice(i);
                      setError("");
                      track("create_voice", v.t);
                      later(() => setStep((s) => (s === 0 ? 1 : s)));
                    }}
                    className={`relative rounded-2xl border p-4 flex flex-col items-center gap-2.5 transition-all ${active
                      ? "border-[#2BA8FF] bg-[#0E1B33]"
                      : "border-white/10 bg-[#0B1220] hover:border-white/25"}`}>
                    <span className="text-3xl leading-none">{VOICE_ICONS[i]}</span>
                    <span className="font-bold text-sm">{v.t}</span>
                    <span className="text-[11px] text-[#9FB0C7] text-center leading-snug">{v.d}</span>
                    <span className={`absolute top-2 right-2 grid place-items-center w-5 h-5 rounded-full border ${active
                      ? "border-[#2BA8FF] bg-[#2BA8FF]" : "border-white/20"}`}>
                      {active && <Check size={11} className="text-white" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 0 && mode === "bot" && (
          <>
            {bPage === "event" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_ev_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_ev_sub}</p>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {eventsOrdered.map((e) => {
                    const active = bEvent === e.key;
                    const special = e.key === "custom" || e.key === "nooccasion";
                    return (
                      <Fragment key={e.key}>
                        <button data-testid={`wizard-event-${e.key}`}
                          onClick={() => {
                            setBEvent(e.key); setError("");
                            track("create_event", e[lang] || e.ua);
                            if (e.key !== "custom") {
                              later(() => setBPage((p) => (p === "event" ? "genre" : p)));
                            }
                          }}
                          className={`${special ? specialCardCls(active) : cardCls(active)} p-3.5 text-sm font-semibold`}>
                          <span className={ONE_LINE}>{e[lang] || e.ua}</span>
                        </button>
                        {e.key === "custom" && bEvent === "custom" && (
                          <div className="col-span-2 lg:col-span-3">
                            <input value={bEventText} maxLength={200}
                              onChange={(ev) => {
                                const v = ev.target.value;
                                setBEventText(v); setError("");
                                if (v.trim().length >= 3 && !pulseNext) {
                                  setPulseNext(true);
                                  setTimeout(() => setPulseNext(false), 2600);
                                }
                              }}
                              placeholder={c.b_ev_custom_ph}
                              className="w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none px-4 py-3.5 text-base" />
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              </>
            )}

            {bPage === "genre" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_genre_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_genre_sub}</p>
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                  {SOUND_KEYS.map((key, i) => {
                    const active = bGenre === key;
                    return (
                      <button key={key} data-testid={`wizard-genre-${key}`}
                        onClick={() => {
                          setBGenre(key); setError("");
                          track("create_genre", c.sounds[i].t);
                          later(() => setBPage((p) => (p === "genre" ? "mood" : p)));
                        }}
                        className={`${cardCls(active)} px-4 py-3.5 flex gap-3.5 items-start`}>
                        <span className="text-2xl leading-none mt-0.5 shrink-0">{SOUND_ICONS[i]}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block font-bold text-sm">{c.sounds[i].t}</span>
                          <span className="block text-xs text-[#9FB0C7] mt-1 leading-relaxed">{c.sounds[i].d}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {bPage === "more" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_genre_more}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_genre_sub}</p>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {(cat?.genres_extra || []).map((g) => {
                    const active = bGenre === g.key;
                    return (
                      <button key={g.key} data-testid={`wizard-genre-${g.key}`}
                        onClick={() => { setBGenre(g.key); setError(""); track("create_genre", g[lang] || g.ua); setBPage("genre"); }}
                        className={`${cardCls(active)} p-3.5 text-sm font-semibold`}>
                        <span className={ONE_LINE}>{g[lang] || g.ua}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {bPage === "mix" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_mix_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_mix_sub}</p>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {allGenres.map((g) => {
                    const active = bMix.includes(g.key);
                    return (
                      <button key={g.key} data-testid={`wizard-mix-${g.key}`}
                        onClick={() => {
                          setError("");
                          setBMix((prev) => prev.includes(g.key)
                            ? prev.filter((k) => k !== g.key)
                            : (prev.length >= 2 ? prev : [...prev, g.key]));
                        }}
                        className={`${cardCls(active)} p-3 text-xs font-semibold`}>
                        <span className={ONE_LINE}>{g[lang] || g.ua}</span>
                      </button>
                    );
                  })}
                </div>
                <button data-testid="wizard-mix-done" disabled={bMix.length !== 2}
                  onClick={() => {
                    track("create_genre_mix", bMix.map((k) => stripEmoji(catLabel(allGenres, k, lang))).join(" + "));
                    setBPage("mood");
                  }}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl w-full mt-4 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  {c.b_mix_done}
                </button>
              </>
            )}

            {bPage === "custom" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_custom_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_custom_sub}</p>
                <textarea value={bGenreText} rows={4} maxLength={200}
                  onChange={(e) => { setBGenreText(e.target.value); setError(""); }}
                  placeholder={c.b_custom_ph}
                  className="mt-5 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-4 text-base leading-relaxed resize-none" />
              </>
            )}

            {bPage === "mood" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_mood_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_mood_sub}</p>
                <div className="mt-5 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {(cat?.moods || []).map((m) => {
                    const active = bMood === m.key;
                    return (
                      <button key={m.key} data-testid={`wizard-mood-${m.key}`}
                        onClick={() => {
                          setBMood(m.key); setError("");
                          track("create_mood", stripEmoji(m[lang] || m.ua));
                          later(() => setBPage((p) => (p === "mood" ? "lang" : p)));
                        }}
                        className={`${cardCls(active)} p-3.5 text-sm font-semibold`}>
                        <span className={ONE_LINE}>{m[lang] || m.ua}</span>
                      </button>
                    );
                  })}
                  <button data-testid="wizard-mood-custom"
                    onClick={() => { setBMood("custom"); setError(""); }}
                    className={`${cardCls(bMood === "custom")} p-3.5 text-sm font-semibold`}>
                    <span className={ONE_LINE}>✍️ {c.b_mood_custom}</span>
                  </button>
                </div>
                {bMood === "custom" && (
                  <input value={bMoodText} maxLength={200}
                    onChange={(e) => { setBMoodText(e.target.value); setError(""); }}
                    placeholder={c.b_mood_custom_ph}
                    className="mt-3 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none px-4 py-3.5 text-base" />
                )}
              </>
            )}

            {bPage === "lang" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_lang_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_lang_sub}</p>
                <div className="mt-6 grid grid-cols-3 gap-2.5">
                  {(cat?.langs || []).map((l) => {
                    const active = bLang === l.key;
                    return (
                      <button key={l.key} data-testid={`wizard-lang-${l.key}`}
                        onClick={() => {
                          setBLang(l.key); setError("");
                          track("create_lang", stripEmoji(l[lang] || l.ua));
                          later(() => setBPage((p) => (p === "lang" ? "voice" : p)));
                        }}
                        className={`${cardCls(active)} p-4 text-sm font-semibold`}>
                        <span className={ONE_LINE}>{l[lang] || l.ua}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {bPage === "voice" && (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.b_voice_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.b_voice_sub}</p>
                <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {(cat?.voices || []).map((v) => {
                    const active = bVoice === v.key;
                    return (
                      <button key={v.key} data-testid={`wizard-voice-${v.key}`}
                        onClick={() => {
                          setBVoice(v.key); setError("");
                          track("create_voice", catLabel(cat?.voices, v.key, lang));
                          later(() => setStep((s) => (s === 0 ? 1 : s)));
                        }}
                        className={`${cardCls(active)} p-4 flex flex-col items-center gap-2`}>
                        <span className="text-2xl leading-none">{VOICE_EMOJI[v.key] || "🎤"}</span>
                        <span className="font-bold text-sm text-center whitespace-nowrap overflow-hidden w-full">
                          {stripEmoji(v[lang] || v.ua)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.desc_title}</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">{c.desc_sub}</p>
            <textarea data-testid="wizard-desc-input" value={desc} rows={7} maxLength={3000}
              onChange={(e) => { setDesc(e.target.value); setError(""); }}
              placeholder={c.desc_ph}
              className="mt-5 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-4 text-base leading-relaxed resize-none" />
            {mode !== "bot" && (
              <span data-testid="wizard-lang-hint"
                className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/30 rounded-full px-3.5 py-1.5">
                <Check size={13} /> {c.desc_lang_hint}
              </span>
            )}
            <div className="flex items-center justify-between text-xs text-[#647089] font-mono mt-2 gap-4">
              <button data-testid="wizard-example-toggle" onClick={() => setShowExample((v) => !v)}
                className="text-[#2BA8FF] hover:underline">
                {showExample ? c.desc_example_hide : c.desc_example_btn}
              </button>
              <span>{desc.trim().length}/3000</span>
            </div>
            {showExample && (
              <blockquote className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-[#C6D3E6] leading-relaxed">
                {c.desc_example}
              </blockquote>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.co_delivery}</h2>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[#FFD400] border border-[#FFD400]/30 rounded-full px-2 py-0.5">
                {c.co_required}
              </span>
            </div>
            <p className="text-sm text-[#9FB0C7] mt-2">{c.checkout_sub}</p>
            <p className="mt-1.5 text-xs text-[#647089]">{c.co_delivery_hint}</p>

            <div className={`mt-5 grid gap-2.5 ${enSite ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`}>
                {deliveryKeys.map((k) => {
                  const i = DELIVERY_KEYS.indexOf(k);
                  const active = delivery === k;
                  return (
                    <button key={k} data-testid={`wizard-delivery-${k}`}
                      onClick={() => { setDelivery(k); setError(""); }}
                      className={`relative rounded-2xl border p-3.5 flex flex-col items-center gap-2.5 transition-all outline-none focus:outline-none ${active
                        ? "border-[#2BA8FF] bg-[#0E1B33]"
                        : "border-white/10 bg-[#0B1220] hover:border-white/25"}`}
                      style={active ? { boxShadow: "0 10px 30px rgba(43,168,255,0.18)" } : {}}>
                      {active && (
                        <span className="absolute top-2 right-2 grid place-items-center w-5 h-5 rounded-full bg-[#2BA8FF]">
                          <Check size={12} className="text-white" />
                        </span>
                      )}
                      <img src={SOCIAL_ICONS[i]} alt="" className="w-9 h-9 object-contain"
                        style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.5))" }} />
                      <span className="text-xs font-semibold">{c.methods[k]}</span>
                    </button>
                  );
                })}
              </div>
              <input value={contact} maxLength={120}
                inputMode={delivery === "email" ? "email" : (delivery === "instagram" || delivery === "telegram") ? "text" : "tel"}
                onChange={(e) => { setContact(e.target.value); setError(""); }}
                placeholder={c.contact_ph[delivery || "telegram"]}
                className={`mt-3 w-full rounded-2xl bg-[#0B1220] border outline-none px-4 py-3.5 text-base ${contactProblem ? "border-[#FF8A8A]/60" : "border-white/10 focus:border-[#2BA8FF]"}`} />
              {contactProblem && (
                <p data-testid="wizard-contact-error" className="mt-2 text-xs text-[#FF8A8A]">
                  {c[contactProblem]}
                </p>
              )}
          </>
        )}

        {step === 3 && (
          <>
            {enSite ? (
              <>
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.wallet_title}</h2>
                <p className="text-sm text-[#9FB0C7] mt-2">{c.wallet_sub}</p>

                <div className="mt-6 rounded-2xl border border-white/10 bg-[#0B1220] p-5">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">{c.pay_amount}</div>
                      <div className="text-2xl font-display font-extrabold text-[#10B981] mt-1">{mode === "studio" ? activeTier.price : c.price200}</div>
                    </div>
                    <span className="rounded-full border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-wider text-[#6EE7B7]">
                      One-time
                    </span>
                  </div>

                  {walletSent ? (
                    <div className="mt-5 rounded-2xl border border-[#10B981]/25 bg-[#10B981]/10 py-8 px-5 text-center">
                      <div className="mx-auto grid place-items-center w-14 h-14 rounded-full bg-gradient-to-br from-[#22C55E] to-[#10B981]">
                        <Check size={26} className="text-white" />
                      </div>
                      <h3 className="mt-4 font-display text-xl font-extrabold">{c.wallet_sent_title}</h3>
                      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[#9FB0C7]">{c.wallet_sent_text}</p>
                    </div>
                  ) : walletFailed ? (
                    <div className="mt-5">
                      <div className="rounded-2xl border border-[#FFD400]/30 bg-[#FFD400]/10 p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-[#FFD84D]" />
                          <div>
                            <h3 className="font-display text-lg font-extrabold">{c.wallet_error_title}</h3>
                            <p className="mt-1 text-sm leading-relaxed text-[#C6D3E6]">{c.wallet_error_text}</p>
                          </div>
                        </div>
                      </div>

                      {!walletFormOpen ? (
                        <button type="button" data-testid="wallet-contact-open"
                          onClick={() => { setWalletFormOpen(true); setWalletError(""); }}
                          className="mt-4 w-full rounded-2xl border border-[#2BA8FF]/40 bg-[#2BA8FF]/10 px-6 py-3.5 font-bold text-[#8CCBFF] transition-colors hover:bg-[#2BA8FF]/20 flex items-center justify-center gap-2.5">
                          <Mail size={17} /> {c.wallet_contact_btn}
                        </button>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-white/10 bg-[#08111F] p-4">
                          <h3 className="font-display text-lg font-extrabold">{c.wallet_form_title}</h3>
                          <p className="mt-1 text-xs leading-relaxed text-[#9FB0C7]">{c.wallet_form_sub}</p>

                          <label htmlFor="wallet-email" className="mt-4 block text-[10px] font-mono uppercase tracking-wider text-[#8FA0BA]">{c.wallet_form_email}</label>
                          <div className="relative mt-2">
                            <Mail size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#647089]" />
                            <input id="wallet-email" data-testid="wallet-email" type="email" inputMode="email"
                              autoComplete="email" value={walletEmail} maxLength={120}
                              onChange={(e) => { setWalletEmail(e.target.value); setWalletError(""); }}
                              placeholder={c.contact_ph.email}
                              className="w-full rounded-2xl border border-white/10 bg-[#0B1220] py-3 pl-11 pr-4 text-base outline-none transition-colors placeholder:text-[#52627A] focus:border-[#2BA8FF]" />
                          </div>

                          <label htmlFor="wallet-message" className="mt-3 block text-[10px] font-mono uppercase tracking-wider text-[#8FA0BA]">{c.wallet_form_message}</label>
                          <textarea id="wallet-message" data-testid="wallet-message" rows={3} maxLength={2000}
                            value={walletMessage}
                            onChange={(e) => { setWalletMessage(e.target.value); setWalletError(""); }}
                            placeholder={c.wallet_form_ph}
                            className="mt-2 w-full resize-none rounded-2xl border border-white/10 bg-[#0B1220] p-4 text-base leading-relaxed outline-none transition-colors placeholder:text-[#52627A] focus:border-[#2BA8FF]" />

                          {walletError && <p data-testid="wallet-error" className="mt-2 text-sm text-[#FF8A8A]">{walletError}</p>}

                          <button type="button" data-testid="wallet-submit" onClick={submitWalletLead} disabled={walletSending}
                            className="mt-3 w-full rounded-2xl bg-gradient-to-r from-[#22C55E] to-[#10B981] px-6 py-3.5 text-sm font-bold text-white transition-opacity disabled:cursor-wait disabled:opacity-60 flex items-center justify-center gap-2">
                            {walletSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {walletSending ? c.wallet_form_sending : c.wallet_form_send}
                          </button>
                          <p className="mt-3 text-center text-[11px] leading-relaxed text-[#647089]">{c.wallet_form_note}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="mt-5 text-sm text-[#9FB0C7]">{c.wallet_choose}</p>
                      {walletBusy ? (
                        <div data-testid="wallet-processing" className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-9 text-center">
                          <Loader2 size={28} className="mx-auto animate-spin text-[#2BA8FF]" />
                          <p className="mt-4 font-display text-lg font-extrabold">{c.wallet_processing}</p>
                          <p className="mt-1 text-xs text-[#9FB0C7]">{c.wallet_processing_sub}</p>
                        </div>
                      ) : (
                        <div className="mt-4 grid gap-3">
                          <button type="button" data-testid="wallet-apple-pay" onClick={() => startWalletPayment("apple")}
                            className="w-full rounded-2xl border border-white/15 bg-black px-6 py-4 text-white transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2.5">
                            <Wallet size={19} />
                            <span className="font-display text-lg font-bold tracking-tight">Apple Pay</span>
                          </button>
                          <button type="button" data-testid="wallet-google-pay" onClick={() => startWalletPayment("google")}
                            className="w-full rounded-2xl border border-white/20 bg-white px-6 py-4 text-[#1F1F1F] transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-2.5">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-[#F1F3F4] font-display text-base font-extrabold" style={{ color: "#4285F4" }}>G</span>
                            <span className="font-display text-lg font-bold tracking-tight">Google Pay</span>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.pay_title}</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">{c.pay_sub}</p>

            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0B1220] p-5 space-y-5">
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">{c.pay_amount}</div>
                <div className="text-2xl font-display font-extrabold text-[#10B981] mt-1">{mode === "studio" ? activeTier.price : c.price200}</div>
              </div>
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">{c.pay_card}</div>
                {cardLoading ? (
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#9FB0C7]">
                    <Loader2 size={14} className="animate-spin" /> {c.loading_card}
                  </div>
                ) : card ? (
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 min-w-0 rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-base font-mono tracking-wide">{card}</code>
                    <button data-testid="wizard-copy-card" onClick={copyCard} title={c.copy}
                      className="h-11 px-3 rounded-xl border border-white/10 hover:border-[#2BA8FF]/60 transition-colors flex items-center justify-center gap-1.5 text-sm font-bold text-[#C6D3E6] shrink-0 whitespace-nowrap">
                      {copied ? <Check size={16} className="text-[#10B981]" /> : <Copy size={16} />}
                      {c.copy}
                    </button>
                  </div>
                ) : <div className="mt-2 text-sm text-[#9FB0C7]">—</div>}
              </div>
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">{c.pay_receipt}</div>
                <input ref={fileRef} data-testid="wizard-receipt-input" type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={pickReceipt} />
                <button onClick={() => fileRef.current?.click()}
                  className="mt-2 w-full rounded-2xl border border-dashed border-white/20 hover:border-[#2BA8FF]/60 bg-white/[0.02] px-4 py-6 transition-colors">
                  {preview ? (
                    receipt && receipt.type === "application/pdf" ? (
                      <span className="flex flex-col items-center gap-2 text-[#9FB0C7] text-sm">
                        <FileText size={26} className="text-[#2BA8FF]" />
                        {receipt.name}
                        <span className="text-xs text-[#647089]">PDF · до 10 МБ</span>
                      </span>
                    ) : (
                      <img src={preview} alt="" className="max-h-56 mx-auto rounded-xl" />
                    )
                  ) : (
                    <span className="flex flex-col items-center gap-2 text-[#9FB0C7] text-sm">
                      <FileImage size={26} className="text-[#2BA8FF]" />
                      {c.receipt_pick}
                      <span className="text-xs text-[#647089]">{c.receipt_hint}</span>
                    </span>
                  )}
                </button>
                {preview && (
                  <button onClick={() => fileRef.current?.click()}
                    className="mt-2 text-xs text-[#2BA8FF] hover:underline">
                    {c.receipt_change}
                  </button>
                )}
              </div>
              <div data-testid="wizard-eta" className="pt-4 border-t border-white/5">
                {mode === "studio" ? (
                  <>
                    <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider text-center">{c.tier_title}</div>
                    <div className="mt-2.5 space-y-2">
                      {tiers.map((tt) => {
                        const active = tier === tt.key;
                        return (
                          <button key={tt.key} data-testid={`wizard-tier-${tt.key}`}
                            onClick={() => setTier(tt.key)}
                            className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all ${active
                              ? "border-[#10B981] bg-[#10B981]/10"
                              : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                            <span className={`grid place-items-center w-4 h-4 rounded-full border shrink-0 ${active
                              ? "border-[#10B981] bg-[#10B981]" : "border-white/25"}`}>
                              {active && <Check size={10} className="text-white" />}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-bold">{tt.label}</span>
                              <span className="block text-[11px] text-[#647089] font-mono">{tt.eta}</span>
                            </span>
                            <span className="text-sm font-display font-extrabold text-[#10B981] whitespace-nowrap">{tt.price}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">{c.co_eta}</div>
                    <div className="mt-1.5 flex items-center justify-center gap-1.5 text-sm font-bold text-[#10B981]">
                      <Zap size={14} /> {c.co_eta_value}
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            <div className="mt-4 rounded-3xl border border-white/10 bg-[#0B1220] overflow-hidden">
              <button data-testid="wizard-details-toggle" onClick={() => setShowBrief((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left">
                <span className="flex items-center gap-2.5 text-sm font-semibold">
                  <FileText size={15} className="text-[#2BA8FF] shrink-0" /> {c.co_details_title}
                </span>
                <ChevronDown size={17} className={`transition-transform shrink-0 ${showBrief ? "rotate-180 text-[#2BA8FF]" : "text-[#647089]"}`} />
              </button>
              {showBrief && (
                <div className="px-4 pb-4 border-t border-white/5">
                  <div className="pt-3 space-y-2.5">
                    {mode === "bot" ? (
                      <>
                        <BriefRow icon={<Music4 size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.b_row_event} value={bEvent ? catLabel(cat?.events, bEvent, lang) : "—"} />
                        <BriefRow icon={<Music4 size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.b_row_genre} value={
                            bGenre === "mix"
                              ? bMix.map((k) => catLabel(allGenres, k, lang)).join(" + ")
                              : bGenre === "custom"
                                ? (bGenreText.trim() || "—")
                                : catLabel(allGenres, bGenre, lang)} />
                        <BriefRow icon={<Music4 size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.b_row_mood} value={
                            bMood === "custom"
                              ? (bMoodText.trim() || "—")
                              : bMood ? catLabel(cat?.moods, bMood, lang) : "—"} />
                        <BriefRow icon={<Music4 size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.b_row_lang} value={catLabel(cat?.langs, bLang, lang)} />
                        <BriefRow icon={<Mic size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.vo_title} value={catLabel(cat?.voices, bVoice, lang)} />
                      </>
                    ) : (
                      <>
                        <BriefRow icon={<Music4 size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.co_sound} value={sound != null ? c.sounds[sound].t : "—"} />
                        <BriefRow icon={<Mic size={14} className="text-[#2BA8FF] shrink-0" />}
                          label={c.vo_title} value={voice != null ? c.voices[voice].t : "—"} />
                      </>
                    )}
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2.5 text-[11px] text-[#647089] font-mono uppercase tracking-wider">
                        <Clock size={14} className="text-[#2BA8FF] shrink-0" /> {c.co_eta}
                      </span>
                      <span className="shrink-0 text-sm font-semibold text-right whitespace-nowrap">{mode === "studio" ? activeTier.eta : c.co_eta_value}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2.5 text-[11px] text-[#647089] font-mono uppercase tracking-wider">
                        <Wallet size={14} className="text-[#2BA8FF] shrink-0" /> {c.co_total}
                      </span>
                      <span className="text-xl font-display font-extrabold text-[#10B981]">{mode === "studio" ? activeTier.price : c.price200}</span>
                    </div>
                    <div>
                      <span className="flex items-center gap-2.5 text-[11px] text-[#647089] font-mono uppercase tracking-wider">
                        <FileText size={14} className="text-[#2BA8FF] shrink-0" /> {c.co_brief}
                      </span>
                      <div className="flex items-start gap-3 mt-0.5">
                        <p className="flex-1 min-w-0 break-words text-sm text-[#C6D3E6] leading-relaxed">{desc.trim()}</p>
                        <button data-testid="wizard-brief-edit"
                          onClick={() => { setError(""); setStep(1); }}
                          className="text-xs text-[#9FB0C7] hover:text-white transition-colors shrink-0">
                          {c.co_edit}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </>
        )}

        {step === 4 && (
          <div className="text-center pt-10">
            <div className="mx-auto grid place-items-center w-20 h-20 rounded-full grad-tg">
              <Check size={38} className="text-white" />
            </div>
            <h2 className="mt-6 font-display text-3xl font-extrabold uppercase">{c.done_title}</h2>
            <p className="text-[#9FB0C7] mt-2">{c.done_sub}</p>
            <div data-testid="wizard-done-number"
              className="mt-6 inline-block rounded-2xl border border-[#2BA8FF]/40 bg-[#0E1B33] px-6 py-3 font-mono text-xl">
              {c.done_order}<b>{orderNumber}</b>
            </div>
            <p className="max-w-md mx-auto text-sm text-[#9FB0C7] mt-5 leading-relaxed">{c.done_note}</p>
            <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://t.me/episnyasupport" target="_blank" rel="noreferrer"
                data-testid="wizard-done-contact"
                className="w-full sm:w-auto rounded-2xl border border-[#2BA8FF]/40 bg-[#2BA8FF]/10 text-[#8CCBFF] font-bold px-7 py-3.5 flex items-center justify-center gap-2.5 hover:bg-[#2BA8FF]/20 transition-colors">
                <img src="/icons/social/telegram.svg" alt="Telegram" className="w-5 h-5 object-contain" />
                {c.done_contact}
              </a>
              <button onClick={() => { window.dispatchEvent(new Event("episnya:open-myorders")); }}
                data-testid="wizard-myorders"
                className="w-full sm:w-auto rounded-2xl border border-[#FFD400]/45 bg-[#FFD400]/12 text-[#FFD84D] font-bold px-7 py-3.5 flex items-center justify-center gap-2.5 hover:bg-[#FFD400]/20 transition-colors">
                🎧 Мої замовлення
              </button>
              <button onClick={onClose}
                className="w-full sm:w-auto btn-tg grad-tg text-white font-bold px-8 py-3.5 rounded-2xl">
                {c.done_close}
              </button>
            </div>
          </div>
        )}

        {step < 4 && !(step === 3 && enSite) && (
          <div className="sticky bottom-0 z-20 -mx-5 mt-9 px-5 pt-3 bg-[#060A14]/85 backdrop-blur-md border-t border-white/5"
            style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
            {error && <p className="text-sm text-[#FF8A8A] mb-3">{error}</p>}
            <div className="flex items-center gap-3">
              {(step > 0 || (mode === "bot" ? bPage !== "event" : pickPage === "voice")) && (
                <button onClick={goBack}
                  className="rounded-2xl border border-white/10 px-5 py-3.5 font-bold text-sm text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
                  {c.back}
                </button>
              )}
              {step === 0 && mode === "bot" && (() => {
                if (bPage === "more" || bPage === "mix") return null;
                const canNext =
                  (bPage === "event" && bEvent && (bEvent !== "custom" || bEventText.trim().length >= 3)) ||
                  (bPage === "genre" && bGenre && bGenre !== "custom" && bGenre !== "mix") ||
                  (bPage === "custom" && bGenreText.trim().length >= 3) ||
                  (bPage === "mood" && bMood && (bMood !== "custom" || bMoodText.trim().length >= 2)) ||
                  (bPage === "lang") ||
                  (bPage === "voice" && bVoice);
                const next = () => {
                  if (bPage === "event") setBPage("genre");
                  else if (bPage === "custom") {
                    track("create_genre_custom", bGenreText.trim());
                    setBPage("mood");
                  } else if (bPage === "genre") setBPage("mood");
                  else if (bPage === "mood") {
                    if (bMood === "custom") track("create_mood_custom", bMoodText.trim());
                    setBPage("lang");
                  } else if (bPage === "lang") setBPage("voice");
                  else if (bPage === "voice") {
                    setStep(1);
                  }
                };
                return (
                  <button data-testid="wizard-next" disabled={!canNext} onClick={next}
                    className={`btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none ${pulseNext && bPage === "event" ? "animate-pulse" : ""}`}>
                    {c.next} <ArrowRight size={17} />
                  </button>
                );
              })()}
        {step === 0 && mode !== "bot" && pickPage === "sound" && (
                <button data-testid="wizard-next" disabled={sound == null}
                  onClick={() => setPickPage("voice")}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  {c.next} <ArrowRight size={17} />
                </button>
              )}
        {step === 0 && mode !== "bot" && pickPage === "voice" && (
                <button data-testid="wizard-next" disabled={voice == null}
                  onClick={() => setStep(1)}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  {c.next} <ArrowRight size={17} />
                </button>
              )}

              {step === 1 && (
                <button data-testid="wizard-next" disabled={desc.trim().length < 25}
                  onClick={() => {
                    track("create_facts", desc.trim().slice(0, 700));
                    api.post("/public/description", {
                      description: desc.trim(),
                      client_id: getClientId(),
                      source: "сайт",
                    }).catch(() => {});
                    api.post("/public/landing/draft", {
                      ...orderPayload(),
                      description: desc.trim(),
                      client_id: getClientId(),
                      progress: "delivery",
                    }).catch(() => {});
                    setStep(2);
                  }}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  {c.next} <ArrowRight size={17} />
                </button>
              )}
              {step === 2 && (
                <button data-testid="wizard-confirm" disabled={!delivery || !contactOk || transition}
                  onClick={confirmCheckout}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  {c.co_confirm} <ArrowRight size={17} />
                </button>
              )}
              {step === 3 && (
                <button data-testid="wizard-submit" disabled={!receipt || submitting} onClick={submit}
                  className="font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none"
                  style={{
                    background: "linear-gradient(135deg,#10B981,#059669)",
                    boxShadow: "0 10px 34px rgba(16,185,129,0.35)",
                  }}>
                  {submitting ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
                  {submitting ? c.submitting : c.submit}
                </button>
              )}
              {step === 3 && submitting && (
                <div className="w-full mt-3" data-testid="upload-progress">
                  <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${uploadPct || 0}%`, background: "linear-gradient(90deg,#2BA8FF,#10B981)" }} />
                  </div>
                  <div className="mt-1.5 text-center text-xs text-[#9FB0C7]">
                    {uploadPct >= 100 ? "Обробляємо чек…" : `Завантажуємо чек… ${uploadPct || 0}%`}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <>
            <div className="mt-4 rounded-2xl border border-white/10 bg-[#0B1220] overflow-hidden">
              <button data-testid="wizard-guarantee" onClick={() => setGuaranteeOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left">
                <span className="flex items-center gap-2.5 text-sm font-semibold">
                  <ShieldCheck size={16} className="text-[#10B981] shrink-0" /> {c.guarantee_q}
                </span>
                <ChevronDown size={17} className={`transition-transform shrink-0 ${guaranteeOpen ? "rotate-180 text-[#2BA8FF]" : "text-[#647089]"}`} />
              </button>
              {guaranteeOpen && (
                <p className="px-4 pb-4 text-sm text-[#9FB0C7] leading-relaxed">{c.guarantee_a}</p>
              )}
            </div>
            {!enSite && (
              <a href="https://t.me/episnyasupport" target="_blank" rel="noreferrer"
                data-testid="wizard-support"
                className="mt-3 w-full rounded-2xl border border-[#2BA8FF]/40 bg-[#2BA8FF]/10 text-[#8CCBFF] font-bold px-6 py-3.5 flex items-center justify-center gap-2.5 hover:bg-[#2BA8FF]/20 transition-colors">
                <img src="/icons/social/telegram.svg" alt="Telegram" className="w-5 h-5 object-contain" />
                {c.support_cta}
              </a>
            )}
          </>
        )}

        {(transition || submitting) && (
          <div className="fixed inset-0 z-[95] grid place-items-center bg-[#060A14]/85 backdrop-blur-sm px-6">
            <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0E1526] p-7 text-center"
              style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.55)" }}>
              <div className="mx-auto grid place-items-center w-16 h-16 rounded-full grad-tg">
                <Loader2 size={26} className="animate-spin text-white" />
              </div>
              <p className="mt-5 font-display font-extrabold uppercase text-sm tracking-wide">{c.studio_title}</p>
              <p className="mt-2 text-xs text-[#9FB0C7]">{c.studio_sub}</p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function VariantScreen({ open, onClose, onPickDev, onPickBot, c }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[85] bg-[#060A14] overflow-y-auto" data-testid="create-variant-modal" role="dialog" aria-modal="true">
      <div className="pointer-events-none fixed inset-0 hero-flag opacity-70" />
      <div className="sticky top-0 z-10 bg-[#060A14]/80 backdrop-blur"
        style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}>
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-[#9FB0C7] font-mono uppercase tracking-wider">
            <Music4 size={15} className="text-[#2BA8FF]" /> E-PISNYA
          </div>
          <button data-testid="create-variant-close" onClick={onClose}
            className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors"
            aria-label={c.done_close}>
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="relative max-w-3xl mx-auto px-5 pt-8 sm:pt-12 fade-up"
        style={{ paddingBottom: "max(48px, env(safe-area-inset-bottom))" }}>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">{c.modal_title}</h2>
        <p className="text-sm text-[#9FB0C7] mt-2">{c.modal_sub}</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-7">
          <OptionCard testid="create-variant-dev" accent="yellow"
            title={c.dev_title} desc={c.dev_desc} price={c.dev_price} badge={c.dev_badge}
            onClick={onPickDev}
            corner={(
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#8CCBFF]">
                <Clock size={13} /> {c.dev_eta}
              </span>
            )} />
          <OptionCard testid="create-variant-bot" accent="blue"
            title={c.bot_title} desc={c.bot_desc} price={c.bot_price} badge={c.bot_badge}
            onClick={onPickBot}
            bg={{
              backgroundColor: "#0B1220",
              backgroundImage: ("linear-gradient(115deg, rgba(43,168,255,0.02) 0%, "
                + "rgba(43,168,255,0.10) 55%, rgba(43,168,255,0.22) 100%)"),
              backgroundClip: "padding-box",
            }}
            corner={(
              <img src="/icons/social/telegram.svg" alt="Telegram"
                className="w-10 h-10 object-contain"
                style={{ filter: "drop-shadow(0 6px 16px rgba(43,168,255,0.5))" }} />
            )} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function CreateSongCta({ goBot, bare }) {
  const { t, enSite } = useLang();
  const c = t.create;
  const [variantsOpen, setVariantsOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeInitial, setResumeInitial] = useState(null);
  const [wizardMode, setWizardMode] = useState("bot");

  useEffect(() => {
    const open = () => {
      if (enSite) {
        setVariantsOpen(false);
        setResumeInitial(null);
        setWizardMode("bot");
        setWizardOpen(true);
      } else {
        setVariantsOpen(true);
      }
    };
    window.addEventListener("episnya:open-create", open);
    return () => window.removeEventListener("episnya:open-create", open);
  }, [enSite]);

  // Виджет «Ваше останнє створення»: открыть конструктор прямо на сохранённом шаге.
  useEffect(() => {
    const onResume = (e) => {
      setVariantsOpen(false);
      setResumeInitial(e.detail || null);
      setWizardMode(enSite ? "bot" : (e.detail?.mode || "studio"));
      setWizardOpen(true);
    };
    window.addEventListener("episnya:resume-create", onResume);
    return () => window.removeEventListener("episnya:resume-create", onResume);
  }, [enSite]);

  // Тарифы на главной открывают визард напрямую (без экрана выбора варианта).
  useEffect(() => {
    const onOpen = (e) => {
      setVariantsOpen(false);
      setResumeInitial(null);
      setWizardMode(e.detail?.mode || "bot");
      setWizardOpen(true);
    };
    window.addEventListener("episnya:open-wizard", onOpen);
    return () => window.removeEventListener("episnya:open-wizard", onOpen);
  }, []);

  const openSiteWizard = () => {
    setVariantsOpen(false);
    setResumeInitial(null);
    setWizardMode("bot");
    setWizardOpen(true);
  };
  const pickDev = () => {
    if (enSite) { openSiteWizard(); return; }
    setVariantsOpen(false);
    setResumeInitial(null);
    api.post("/web/click", { source: "create_dev" }).catch(() => {});
    setWizardMode("studio");
    setWizardOpen(true);
  };
  const pickBot = () => {
    if (enSite) { openSiteWizard(); return; }
    if (goBot) goBot("create_bot");
  };

  return (
    <>
      {!bare && (
        <>
          <button data-testid="create-song-button"
            onClick={() => {
              track("constructor_open_hero");
              if (enSite) openSiteWizard(); else setVariantsOpen(true);
            }}
            className="cta-blink cta-premium btn-tg grad-tg text-white font-display font-extrabold w-full sm:w-auto rounded-[26px] sm:rounded-[30px] pl-4 pr-5 sm:pl-6 sm:pr-8 py-4 sm:py-5 flex items-center justify-center gap-3 sm:gap-4 ring-1 ring-white/25">
            <span className="grid place-items-center w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-white/15 border border-white/25 shrink-0">
              <Music4 size={22} />
            </span>
            <span className="text-lg sm:text-2xl tracking-tight whitespace-nowrap">{t.hero_cta_song}</span>
            <ArrowRight size={22} className="hidden sm:block opacity-90 shrink-0" />
          </button>
          <p className="mt-3 text-xs sm:text-sm text-[#9FB0C7] font-mono">{c.cta_sub}</p>
        </>
      )}

      {!enSite && (
        <VariantScreen open={variantsOpen} onClose={() => setVariantsOpen(false)}
          onPickDev={pickDev} onPickBot={pickBot} c={c} />
      )}

      <Wizard open={wizardOpen}
        mode={wizardMode}
        onClose={() => {
          setWizardOpen(false);
          setResumeInitial(null);
          // Виджет «Ваше останнє створення» обновляется сразу, без перезагрузки.
          window.dispatchEvent(new Event("episnya:widget-refresh"));
        }}
        initial={resumeInitial} />
    </>
  );
}
